
import { defineComponent, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import ProgressSpinner from 'primevue/progressspinner';
import TemplateElementsPreviewImage from '@/components/template-builder/TemplateElementsPreviewImage.vue';
import { UserState } from '@/composables/User';

interface TemplateSection {
  id: string;
  syncId?: string;
  syncName?: string;
}

export default defineComponent({
  name: 'SectionSelectionModal',

  components: {
    Dialog,
    Button,
    ProgressSpinner,
    TemplateElementsPreviewImage,
    Chip,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    templateName: {
      type: String,
      required: true,
    },
    templateConfig: {
      type: String,
      required: true,
    },
    initialSelectedSections: {
      type: Array as () => string[],
      default: () => [],
    },
  },

  emits: ['update:visible', 'sections-selected'],

  setup(props, { emit }) {
    const { t } = useI18n();
    
    const displayModal = ref(props.visible);
    const isLoading = ref(false);
    const sections = ref<TemplateSection[]>([]);
    const selectedSections = ref<Set<string>>(new Set());

    // Track if modal has been initialized to avoid resetting selection
    const isInitialized = ref(false);

    // Watch for prop changes
    watch(() => props.visible, (newValue) => {
      displayModal.value = newValue;
      if (newValue && !isInitialized.value) {
        loadSections();
        // Initialize selected sections from props only on first open
        selectedSections.value = new Set(props.initialSelectedSections);
        isInitialized.value = true;
      } else if (newValue && isInitialized.value) {
        // Just load sections without resetting selection
        loadSections();
      } else if (!newValue) {
        // Reset flag when modal is closed
        isInitialized.value = false;
      }
    });

    const loadSections = async () => {
      isLoading.value = true;
      sections.value = [];
      
      try {
        if (props.templateConfig) {
          const config = JSON.parse(props.templateConfig);
          
          if (config.body_structure && config.body_structure.sections) {
            sections.value = config.body_structure.sections.map((section: any) => ({
              id: section.id_template_elements,
              syncId: section.sync_id,
              syncName: section.sync_name,
            }));
          }
        }
      } catch (error) {
        sections.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const updateDialogVisibility = () => {
      displayModal.value = false;
      emit('update:visible', false);
    };

    const toggleSectionSelection = (sectionId: string) => {
      const newSet = new Set(selectedSections.value);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      selectedSections.value = newSet;
    };

    const handleSectionClick = (sectionId: string) => {
      toggleSectionSelection(sectionId);
    };

    const deselectAllSections = () => {
      selectedSections.value.clear();
    };

    const validateSelection = () => {
      const selectedIds = Array.from(selectedSections.value);
      emit('sections-selected', selectedIds);
      updateDialogVisibility();
    };

    return {
      t,
      displayModal,
      isLoading,
      sections,
      selectedSections,
      updateDialogVisibility,
      toggleSectionSelection,
      handleSectionClick,
      deselectAllSections,
      validateSelection,
      UserState,
    };
  },
});
